import {
    useState,
    useEffect,
    useCallback,
    useRef,
    type FC,
    type FormEventHandler,
    type FocusEvent as ReactFocusEvent,
} from 'react';
import { graphql, useRefetchableFragment } from 'react-relay';
import classnames from 'classnames';

import { FormattedMessage, defineMessages, useIntl } from 'dibs-react-intl';
import { SearchBar } from 'dibs-elements/exports/SearchBar.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import ArrowLeft from 'dibs-icons/exports/legacy/ArrowLeft.ts';
import { handleLocaleUrl, GLOBAL_CLIENT_ONLY_LOCALE } from 'dibs-intl/exports/urls';
import { defaultAvatarGradient } from 'dibs-crypto-utils/exports/formattingUtils.ts';
import { NftItemTileMedia } from 'dibs-nft-item-tile/exports/NftItemTileMedia.ts';
import { useDeviceType } from '../hooks/useDeviceType.tsx';
import { useClientState } from 'dibs-react-hooks/exports/useClientState.ts';
import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback.js';

import { NftSearchBarOverlay } from './NftSearchBarOverlay.tsx';
import { ScrollLock } from './ScrollLock/ScrollLock.tsx';
import {
    dedupeLocalStorage,
    handleRecentStorage,
    cleanUpRecentSearches,
} from './shared/nftSearchBarHelpers.ts';
import {
    SEARCH_PAGE_PATH,
    SEARCH_QUERY_PARAM,
    type SuggestionType,
    type CursorType,
} from './shared/nftSearchBarConstants.ts';

import { type NftSearchBarMobileRefetchQuery } from './__generated__/NftSearchBarMobileRefetchQuery.graphql.ts';
import {
    type NftSearchBarMobile_viewer$key,
    type NftSearchBarMobile_viewer$data,
} from './__generated__/NftSearchBarMobile_viewer.graphql.ts';

import styles from './NftSearchBarMobile.scss';

const SEARCH_BAR_EXPANDED = 'expanded';
const SEARCH_BAR_NONE = 'none';
const MIN_NUM_VALUE_CHARACTERS = 2;
export const SEARCH_BAR_ID = 'nav-search';

const messages = defineMessages({
    placeholder: {
        id: 'NftSearchBar.mobile.placeholder',
        defaultMessage: 'Search',
    },
    closeSearchLabel: {
        id: 'NftSearchBar.mobile.closeSearchLabel',
        defaultMessage: 'Exit search',
    },
});

const refetchableFragment = graphql`
    fragment NftSearchBarMobile_viewer on Viewer
    @refetchable(queryName: "NftSearchBarMobileRefetchQuery")
    @argumentDefinitions(
        query: { type: "String", defaultValue: "" }
        hasQuery: { type: "Boolean", defaultValue: false }
    ) {
        nftSearchSuggestion(query: $query, includeTypes: ["ITEMS", "CREATORS", "EXHIBITIONS"])
            @include(if: $hasQuery) {
            items {
                pdpUrl
                metadata {
                    ... on NftDigitalArtMetadata {
                        name
                    }
                }
                creatorAddress {
                    publicProfile {
                        profileName
                    }
                }
                ...NftItemTileMedia_item
            }
            creators {
                address {
                    address
                    publicProfile {
                        profileName
                        avatarImage {
                            path
                        }
                    }
                    publicProfileUrl
                }
                nftItemCount
            }
            exhibitions {
                pageName
                modules {
                    heroBanner {
                        desktopImage {
                            url
                        }
                        bannerConfig {
                            ... on NftCmsExhibitionHtmlBannerType {
                                eyeBrow
                            }
                        }
                    }
                }
            }
        }
    }
`;

const buildSuggestions = (
    nftSuggestions: NftSearchBarMobile_viewer$data['nftSearchSuggestion']
): SuggestionType[] => {
    const suggestions: SuggestionType[] = [];
    Object.keys(nftSuggestions || {}).forEach(key => {
        if (key === 'items') {
            suggestions.push({
                type: 'ITEMS',
                terms: nftSuggestions?.items?.map(item => {
                    return {
                        term: item?.metadata?.name || '',
                        subterm: item?.creatorAddress?.publicProfile?.profileName || '',
                        urlLabel: item?.pdpUrl || '',
                        image: (
                            <NftItemTileMedia
                                item={item || null}
                                animate={false}
                                isMobile={false}
                                showIcon={false}
                            />
                        ),
                    };
                }),
            });
        }
        if (key === 'creators') {
            suggestions.push({
                type: 'CREATORS',
                terms: nftSuggestions?.creators?.map(creator => {
                    return {
                        term: creator?.address?.publicProfile?.profileName || '',
                        subterm: (
                            <FormattedMessage
                                id="NftSearchBarMobile.creators.nftItemCount"
                                defaultMessage="{nftItemCount, plural, one {# Creation} other {# Creations}}"
                                values={{
                                    nftItemCount: creator?.nftItemCount,
                                }}
                            />
                        ),
                        urlLabel: creator?.address?.publicProfileUrl || '',
                        image: creator?.address?.publicProfile?.avatarImage?.path ? (
                            <img
                                loading="lazy"
                                src={creator.address.publicProfile.avatarImage.path}
                                alt={`avatar-${creator.address.publicProfile.profileName}`}
                            />
                        ) : null,
                        defaultAvatar: defaultAvatarGradient(creator?.address?.address || ''),
                    };
                }),
            });
        }
        if (key === 'exhibitions') {
            suggestions.push({
                type: 'EXHIBITIONS',
                terms: nftSuggestions?.exhibitions?.map(exhibition => {
                    return {
                        term: exhibition?.modules?.heroBanner?.[0]?.bannerConfig?.eyeBrow || '',
                        urlLabel: `/nft-collections/exhibitions/${exhibition?.pageName || ''}`,
                        image: (
                            <img
                                loading="lazy"
                                src={exhibition?.modules?.heroBanner?.[0]?.desktopImage?.url || ''}
                                alt={`avatar-${exhibition?.modules?.heroBanner?.[0]?.bannerConfig?.eyeBrow}`}
                            />
                        ),
                    };
                }),
            });
        }
    });
    return suggestions;
};

type Props = {
    focusOnOverlayScroll: boolean;
    isAnimatedSearchBar: boolean;
    showArrow: boolean;
    showScrollLockedOverlay: boolean;
    viewer: NftSearchBarMobile_viewer$key | null | undefined;
    onlySearchBarIsVisible: boolean;
    setOnlySearchBarIsVisible: (value: boolean) => void;
};

export const NftSearchBarMobile: FC<Props> = ({
    isAnimatedSearchBar,
    showArrow,
    focusOnOverlayScroll,
    showScrollLockedOverlay,
    viewer: viewerRef,
    onlySearchBarIsVisible,
    setOnlySearchBarIsVisible,
}) => {
    const intl = useIntl();
    const isClient = useClientState();
    const { deviceType } = useDeviceType();
    const isTablet = deviceType === 'tablet';

    const [animationType, setAnimationType] = useState(SEARCH_BAR_NONE);
    const [value, setValue] = useState('');
    const [isFocused, setIsFocused] = useState(true);
    const [isSearchBarCleared, setIsSearchBarCleared] = useState(false);
    const [cursorTerms, setCursorTerms] = useState<CursorType[]>([]);
    const [cursorPosition, setCursorPosition] = useState(-1);
    const overlayWrapper = useRef<HTMLDivElement>(null);
    const searchBarWrapper = useRef<HTMLDivElement>(null);
    const [viewer, refetch] = useRefetchableFragment<
        NftSearchBarMobileRefetchQuery,
        NftSearchBarMobile_viewer$key
    >(refetchableFragment, viewerRef);

    const searchSuggestions = viewer?.nftSearchSuggestion;
    const suggestions = buildSuggestions(searchSuggestions);
    const isSearchBarExpanded = animationType === SEARCH_BAR_EXPANDED;
    const recentSearches = dedupeLocalStorage() || [];

    const onFocus = useCallback(() => {
        setIsFocused(true);
        setOnlySearchBarIsVisible(true);
        if (isAnimatedSearchBar && animationType === SEARCH_BAR_NONE) {
            setAnimationType(SEARCH_BAR_EXPANDED);
        }
    }, [
        setIsFocused,
        isAnimatedSearchBar,
        animationType,
        setAnimationType,
        setOnlySearchBarIsVisible,
    ]);

    const onBlur = useCallback(
        (e: ReactFocusEvent<HTMLInputElement>) => {
            const { value: val } = e.target;
            if (value !== val) {
                setValue(val);
            }
        },
        [value]
    );

    const setSearchTermFromParams = useCallback(() => {
        if (location.href.indexOf(SEARCH_PAGE_PATH) !== -1) {
            const searchParams = new URLSearchParams(location.search);
            const searchTerm = searchParams.get(SEARCH_QUERY_PARAM);

            if (searchTerm) {
                setValue(searchTerm);
            }
        }
    }, [setValue]);

    const handleRowClick = useCallback(
        (val: string, href: string) => {
            setValue(val);
            setOnlySearchBarIsVisible(false);
            handleRecentStorage(val, href);
            location.assign(handleLocaleUrl(href, GLOBAL_CLIENT_ONLY_LOCALE));
        },
        [setValue, setOnlySearchBarIsVisible]
    );

    const fetchSuggestions = useCallback(() => {
        refetch(
            { query: value.toLowerCase(), hasQuery: value.length > 1 },
            { fetchPolicy: 'network-only' }
        );
        setIsSearchBarCleared(false);
    }, [value, refetch]);

    const [debouncedFetchSuggestions] = useDebouncedCallback(fetchSuggestions, 250);

    const handleChange = useCallback(
        (newValue: string) => {
            if (!newValue) {
                setIsSearchBarCleared(true);
            }
            setValue(newValue);
            setCursorTerms([]);
            setCursorPosition(-1);
        },
        [setValue, setCursorTerms, setCursorPosition]
    );

    const handleClear = useCallback(() => {
        if (isAnimatedSearchBar) {
            setAnimationType(SEARCH_BAR_NONE);
        }
    }, [isAnimatedSearchBar, setAnimationType]);

    const handleSubmit: FormEventHandler = useCallback(
        event => {
            event.preventDefault();
            let urlLabel;

            if (cursorPosition > -1 && cursorTerms[cursorPosition].link) {
                handleRowClick(
                    cursorTerms[cursorPosition].term,
                    cursorTerms[cursorPosition]?.link || ''
                );
            } else if (value) {
                urlLabel = `/nft-collections/search/?q=${encodeURIComponent(value.toLowerCase())}`;
                handleRecentStorage(value, urlLabel);
                location.assign(handleLocaleUrl(urlLabel, GLOBAL_CLIENT_ONLY_LOCALE));
            }
        },
        [value, cursorTerms, cursorPosition, handleRowClick]
    );
    const handleKeydown = useCallback(
        (e: KeyboardEvent): void => {
            switch (e.key) {
                case 'Esc':
                case 'Escape':
                    // close overlay on [Esc]
                    setOnlySearchBarIsVisible(false);
                    break;

                default:
                    break;
            }
        },
        [setOnlySearchBarIsVisible]
    );
    useEffect(() => {
        if (showArrow && isFocused) {
            document.addEventListener('keydown', handleKeydown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeydown);
        };
    }, [showArrow, onFocus, isFocused, handleKeydown]);

    useEffect(() => {
        cleanUpRecentSearches();
    }, []);

    useEffect(() => {
        if (value) {
            debouncedFetchSuggestions(value);
        }
    }, [value, debouncedFetchSuggestions]);

    useEffect(() => {
        setSearchTermFromParams();
    }, [setSearchTermFromParams]);

    useEffect(() => {
        function handleOutsideFocus(e: FocusEvent): void {
            if (isFocused && !searchBarWrapper.current?.contains(e.target as Node)) {
                setOnlySearchBarIsVisible(false);
            }
        }

        if (isFocused) {
            document.body.addEventListener('focus', handleOutsideFocus, true);
        }
        return () => {
            document.body.removeEventListener('focus', handleOutsideFocus, true);
        };
    }, [isFocused, searchBarWrapper, setOnlySearchBarIsVisible]);

    const showRecentlyViewed =
        suggestions.length === 0 || value.length < MIN_NUM_VALUE_CHARACTERS || isSearchBarCleared;

    const showOverlay = isFocused;
    // if (isAnimatedSearchBar || !isMobileRecentSearchUiDisabled) {
    //     showOverlay = isFocused;
    //     // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    // } else if (isMobileRecentSearchUiDisabled) {
    //     showOverlay = isFocused && !showRecentlyViewed;
    // }

    const wrapperClasses = classnames(styles.wrapper, {
        [styles.animatedSearchBar]: isAnimatedSearchBar,
        [styles.expanded]: isSearchBarExpanded,
        [styles.onlySearchBarIsVisible]: onlySearchBarIsVisible && !isTablet,
    });
    const overlayWrapperClasses = classnames({
        [styles.overlayWrapper]: showOverlay,
    });
    const overlayClasses = styles.suggestionsOverlay;
    const whiteOverlayClasses = classnames(styles.whiteOverlay, {
        [styles.visible]: isFocused,
    });

    const showCancel = onlySearchBarIsVisible && !isTablet;
    const isClearButtonHidden = isAnimatedSearchBar ? !isFocused : !value;

    return (
        <div id={SEARCH_BAR_ID} className={wrapperClasses} ref={searchBarWrapper}>
            <form role="search" action="/nft-collections/search/" onSubmit={handleSubmit}>
                <div className={styles.searchBar}>
                    {showArrow && (
                        <Link
                            className={styles.arrow}
                            onClick={() => setIsFocused(false)}
                            ariaLabel={intl.formatMessage(messages.closeSearchLabel)}
                        >
                            <ArrowLeft />
                        </Link>
                    )}
                    <SearchBar
                        dataTn="search-bar-mobileWeb"
                        hasRightSearchButton={false}
                        focusOnClear={!isAnimatedSearchBar}
                        noBorder={onlySearchBarIsVisible}
                        onBlur={onBlur}
                        onFocus={onFocus}
                        onChange={handleChange}
                        onClear={handleClear}
                        placeholder={intl.formatMessage(messages.placeholder)}
                        size="medium"
                        value={value}
                        isClearButtonHidden={isClearButtonHidden}
                        isHeaderSearch
                        dropdownIsOpen={isFocused}
                    />
                    {showCancel && (
                        <Link
                            className={styles.cancelButton}
                            onClick={() => {
                                setOnlySearchBarIsVisible(false);
                            }}
                            ariaLabel={intl.formatMessage(messages.closeSearchLabel)}
                        >
                            <FormattedMessage
                                id="NftSearchBar.mobile.cancel"
                                defaultMessage="Cancel"
                            />
                        </Link>
                    )}
                </div>
            </form>
            {showScrollLockedOverlay && (
                <ScrollLock isLocked={showOverlay}>
                    <div className={whiteOverlayClasses} />
                </ScrollLock>
            )}
            <div className={overlayWrapperClasses} ref={overlayWrapper}>
                {isClient ? (
                    <NftSearchBarOverlay
                        overlayClassName={overlayClasses}
                        canShowRecentlyViewed={isFocused && showRecentlyViewed}
                        handleRowClick={handleRowClick}
                        focusOnOverlayScroll={focusOnOverlayScroll}
                        visible={showOverlay}
                        suggestions={showRecentlyViewed ? recentSearches : suggestions}
                    />
                ) : null}
            </div>
        </div>
    );
};
